<template>
    <b-modal
        :id="modalId"
        v-model="visible"
        title="Manage Compound Equivalents"
        size="xl"
        @show="onShow"
        @hidden="onHidden"
    >
        <b-alert show variant="info" class="mb-3">
            <i class="fas fa-info-circle"></i>
            Manage equivalency relationships for this compound. Equivalent compounds can be used interchangeably
            and will appear when searching for equivalent prescriptions.
        </b-alert>

        <b-overlay :show="loading" rounded="sm">
            <!-- Current Compound Info -->
            <b-card class="mb-3" v-if="currentCompound">
                <h6>Current Compound</h6>
                <div>
                    <strong>{{ currentCompound.name }}</strong>
                    <br>
                    <small class="text-muted">NDC: {{ currentCompound.ndc }} | ID: {{ currentCompound.compoundID }}</small>
                </div>
            </b-card>

            <!-- Add New Equivalent Section -->
            <b-card class="mb-3">
                <h6>Add Equivalent Compound</h6>
                <b-row class="align-items-end">
                    <b-col cols="9">
                        <SearchComponent
                            v-model="searchModel"
                            label="Search Compound"
                            :search-u-r-l="`/Compound`"
                            :params="{ onlyActive: false }"
                            @input="onCompoundSelected"
                        />
                    </b-col>
                    <b-col cols="3">
                        <b-button
                            variant="primary"
                            block
                            :disabled="!selectedCompound || addingEquivalency"
                            @click="addEquivalency"
                        >
                            <i class="fas fa-plus"></i> Add Equivalent
                        </b-button>
                    </b-col>
                </b-row>
                <div v-if="selectedCompound" class="mt-2">
                    <small class="text-muted">
                        Selected: <strong>{{ selectedCompound.name }}</strong> ({{ selectedCompound.ndc }})
                    </small>
                </div>
            </b-card>

            <!-- Existing Equivalents Table -->
            <div v-if="equivalentCompounds.length > 0">
                <h6 class="mb-2">Current Equivalent Compounds ({{ equivalentCompounds.length }})</h6>

                <b-table
                    :key="tableKey"
                    :items="equivalentCompounds"
                    :fields="tableFields"
                    striped
                    hover
                    small
                >
                    <!-- Compound Name -->
                    <template #cell(name)="data">
                        <div>
                            <strong>{{ data.item.name }}</strong>
                            <br>
                            <small class="text-muted">NDC: {{ data.item.ndc }}</small>
                        </div>
                    </template>

                    <!-- Equivalency Depth Badge -->
                    <template #cell(equivalencyDepth)="data">
                        <b-badge :variant="data.item.equivalencyDepth === 1 ? 'success' : 'info'">
                            {{ data.item.equivalencyDepth === 1 ? 'Direct' : `${data.item.equivalencyDepth} hops` }}
                        </b-badge>
                    </template>

                    <!-- Status -->
                    <template #cell(status)="data">
                        <b-badge :variant="getStatusVariant(data.item.status)">
                            {{ getStatusName(data.item.status) }}
                        </b-badge>
                    </template>

                    <!-- Actions -->
                    <template #cell(actions)="data">
                        <b-button
                            v-if="data.item.equivalencyDepth === 1"
                            size="sm"
                            variant="danger"
                            @click="removeEquivalency(data.item)"
                            :disabled="removingEquivalency"
                        >
                            <i class="fas fa-trash"></i> Remove
                        </b-button>
                        <span v-else class="text-muted">
                            <small>Indirect (via chain)</small>
                        </span>
                    </template>
                </b-table>
            </div>

            <!-- No equivalents -->
            <b-alert v-else show variant="warning">
                <i class="fas fa-exclamation-triangle"></i>
                No equivalent compounds configured. Add equivalents using the search above.
            </b-alert>
        </b-overlay>

        <template #modal-footer="{ hide }">
            <b-button variant="secondary" @click="hide()">
                Close
            </b-button>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import SearchComponent from '@/components/SearchComponent.vue';
import { CompoundDrug } from '@/models/Drug/CompoundDrug';
import { DrugStatus } from '@/models/Drug/Drug';
import Axios, { AxiosError } from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

interface EquivalentCompound {
    compoundID: number;
    ndc: string;
    name: string;
    labelName: string;
    status: DrugStatus.DrugStatusEnum;
    equivalencyDepth: number;
}

@Component({
    name: 'ManageEquivalentsModal',
    components: {
        SearchComponent
    }
})
export default class ManageEquivalentsModal extends Vue {
    @Prop({ required: true }) readonly modalId!: string;
    @Prop({ required: false, default: null }) readonly currentCompound!: CompoundDrug | null;

    private visible = false;
    private loading = false;
    private addingEquivalency = false;
    private removingEquivalency = false;

    private searchModel: CompoundDrug = new CompoundDrug();
    private selectedCompound: CompoundDrug | null = null;
    private equivalentCompounds: EquivalentCompound[] = [];
    private tableKey = 0;

    private tableFields = [
        { key: 'compoundID', label: 'ID', sortable: true },
        { key: 'name', label: 'Compound Name', sortable: true },
        { key: 'equivalencyDepth', label: 'Relationship', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'actions', label: 'Actions', class: 'text-center' }
    ];

    async onShow() {
        if (!this.currentCompound?.compoundID) {
            this.$notification(NotificationOptions.error('No compound selected'));
            this.visible = false;
            return;
        }

        await this.loadEquivalentCompounds();
    }

    onHidden() {
        this.searchModel = new CompoundDrug();
        this.selectedCompound = null;
        this.equivalentCompounds = [];
    }

    async loadEquivalentCompounds() {
        if (!this.currentCompound?.compoundID) return;

        this.loading = true;
        try {
            const response = await Axios.get<EquivalentCompound[]>(
                `/Compound/${this.currentCompound.compoundID}/equivalents`
            );

            // Use Vue.set to ensure reactivity and force array replacement
            this.equivalentCompounds = [...response.data];
            this.tableKey++; // Force table to re-render
        } catch (error) {
            console.error('Error loading equivalent compounds:', error);
            this.$notification(NotificationOptions.error('Failed to load equivalent compounds'));
        } finally {
            this.loading = false;
        }
    }

    onCompoundSelected(compound: CompoundDrug | null) {
        this.selectedCompound = compound;
    }

    async addEquivalency() {
        if (!this.currentCompound?.compoundID || !this.selectedCompound?.compoundID) return;

        if (this.currentCompound.compoundID === this.selectedCompound.compoundID) {
            this.$notification(NotificationOptions.error('A compound cannot be equivalent to itself'));
            return;
        }

        // Check if already equivalent
        if (this.equivalentCompounds.some(ec => ec.compoundID === this.selectedCompound!.compoundID)) {
            this.$notification(NotificationOptions.warning('This compound is already marked as equivalent'));
            return;
        }

        this.addingEquivalency = true;
        try {
            await Axios.post(
                `/Compound/${this.currentCompound.compoundID}/equivalents`,
                this.selectedCompound.compoundID,
                { headers: { 'Content-Type': 'application/json' } }
            );

            this.$notification(NotificationOptions.success(`Successfully added "${this.selectedCompound.name}" as equivalent`));

            // Clear selection
            this.searchModel = new CompoundDrug();
            this.selectedCompound = null;

            // Add a small delay to ensure DB transaction is fully committed
            await new Promise(resolve => setTimeout(resolve, 100));

            // Reload the equivalents list
            await this.loadEquivalentCompounds();

            // Force Vue to update the component
            this.$forceUpdate();
        } catch (error) {
            console.error('Error adding equivalency:', error);
            const axiosError = error as AxiosError;
            const message = axiosError.response?.data || 'Failed to add equivalency';
            this.$notification(NotificationOptions.error(message));
        } finally {
            this.addingEquivalency = false;
        }
    }

    async removeEquivalency(compound: EquivalentCompound) {
        if (!this.currentCompound?.compoundID) return;

        const confirmed = await this.$bvModal.msgBoxConfirm(
            `Are you sure you want to remove the equivalency with "${compound.name}"?`,
            {
                title: 'Confirm Removal',
                okVariant: 'danger',
                okTitle: 'Remove',
                cancelTitle: 'Cancel',
                centered: true
            }
        );

        if (!confirmed) return;

        this.removingEquivalency = true;
        try {
            await Axios.delete(
                `/Compound/${this.currentCompound.compoundID}/equivalents/${compound.compoundID}`
            );

            this.$notification(NotificationOptions.success(`Successfully removed equivalency with "${compound.name}"`));

            // Add a small delay to ensure DB transaction is fully committed
            await new Promise(resolve => setTimeout(resolve, 100));

            // Reload the equivalents list
            await this.loadEquivalentCompounds();

            // Force Vue to update the component
            this.$forceUpdate();
        } catch (error) {
            console.error('Error removing equivalency:', error);
            this.$notification(NotificationOptions.error('Failed to remove equivalency'));
        } finally {
            this.removingEquivalency = false;
        }
    }

    getStatusName(status: DrugStatus.DrugStatusEnum): string {
        return DrugStatus.DrugStatusEnum[status] || 'Unknown';
    }

    getStatusVariant(status: DrugStatus.DrugStatusEnum): string {
        switch (status) {
            case DrugStatus.DrugStatusEnum.Active:
            case DrugStatus.DrugStatusEnum.ActiveAndPreferred:
                return 'success';
            case DrugStatus.DrugStatusEnum.BlockedFromFills:
                return 'danger';
            case DrugStatus.DrugStatusEnum.Inactive:
                return 'warning';
            default:
                return 'secondary';
        }
    }
}
</script>

<style scoped>
.b-overlay {
    min-height: 200px;
}
</style>
